import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { defineMessages, useIntl } from 'react-intl';

import { useCustomer } from '../../../../context';

// ─────────────────────────────────────────────────────────────────────────────

export const useGroupOrderSetupForm = () => {
  const { customer } = useCustomer();
  const { formatMessage } = useIntl();

  // ─────────────────────────────────────────────────────────────────────

  const hostName = useMemo(() => {
    const lastNameInitial = customer.lastName ? `${customer.lastName.charAt(0)}.` : undefined;

    return [customer.firstName, lastNameInitial].filter(Boolean).join(' ');
  }, [customer.firstName, customer.lastName]);

  const rules = useMemo(
    () => ({
      orderName: {
        required: formatMessage(messages.orderNameRequired),
        maxLength: {
          value: FIELD_MAX_LENGTH,
          message: formatMessage(messages.orderNameMaxLength, { max_length: FIELD_MAX_LENGTH }),
        },
      },
      name: {
        required: formatMessage(messages.hostNameRequired),
        maxLength: {
          value: FIELD_MAX_LENGTH,
          message: formatMessage(messages.hostNameMaxLength, { max_length: FIELD_MAX_LENGTH }),
        },
      },
    }),
    [formatMessage],
  );

  // ─────────────────────────────────────────────────────────────────────

  const form = useForm<GroupOrderSetupFormData>({
    defaultValues: { orderName: '', hostName },
    mode: 'onChange',
  });

  // ─────────────────────────────────────────────────────────────────────

  return {
    form,
    rules,
    maxLengths: { orderName: FIELD_MAX_LENGTH, name: FIELD_MAX_LENGTH },
  };
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  orderNameRequired: {
    defaultMessage: 'Group order name is required',
    description: 'Group Order Setup | Group order name required error',
  },
  orderNameMaxLength: {
    defaultMessage: 'Group order name must be {max_length} characters or fewer',
    description: 'Group Order Setup | Group order name max length error',
  },
  hostNameRequired: {
    defaultMessage: 'Your name is required',
    description: 'Group Order Setup | Host name required error',
  },
  hostNameMaxLength: {
    defaultMessage: 'Your name must be {max_length} characters or fewer',
    description: 'Group Order Setup | Host name max length error',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const FIELD_MAX_LENGTH = 50;

// ─── Types ───────────────────────────────────────────────────────────────────

export type GroupOrderSetupFormData = {
  orderName: string;
  hostName: string;
};
